import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { id: "inicio", label: "Inicio" },
  { id: "platos", label: "Platos" },
  { id: "galeria", label: "Galería" },
  { id: "menu", label: "Menú" },
  { id: "eventos", label: "Eventos" },
  { id: "reservar", label: "Reservar" },
  { id: "contacto", label: "Contacto" },
];

export default function Header({ content, scrollTo }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-stone-200/60 shadow-[0_4px_20px_rgba(0,0,0,0.04)]" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <button onClick={() => go("inicio")} className="flex items-center gap-3" data-testid="header-logo">
          <img src={content.logo} alt={content.brand_name} className="h-10 md:h-12 w-auto object-contain" />
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}`}
              className="relative text-sm font-medium text-[#1a1a1a] tracking-wide group whitespace-nowrap"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-[#da251d] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={content.doordash_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-order-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-[#da251d] text-white text-sm font-semibold px-6 py-2.5 transition-transform duration-200 hover:bg-[#b91c1c] active:scale-95"
          >
            Ordenar ahora
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 text-[#1a1a1a]"
            data-testid="mobile-menu-toggle"
            aria-label="Menú"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-stone-200"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-base font-medium border-b border-stone-100 last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={content.doordash_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 text-center rounded-full bg-[#da251d] text-white font-semibold px-6 py-3"
              >
                Ordenar ahora
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
